// Engagements.jsx

import React, { useMemo } from 'react';
import { useTable, useSortBy, useFilters } from 'react-table';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EngagementNotes from '@/components/data/engagements/EngagementNotes';
import HoursDisplay from '@/components/data/hours/HoursDisplay';
import { professionalengagementsCss } from '@digital_monorepo/styling';

const Engagements = ({ engagements, withHours }) => {
  const { t } = useTranslation();

  const columns = useMemo(() => [
    {
      Header: t('ENGAGEMENTS_TABLE.HEADERS.ENGAGEMENT_ID'),
      accessor: 'EngagementID',
      Cell: ({ value }) => <Link to={`/engagements/${value}`}>{value}</Link>,
    },
    {
      Header: t('ENGAGEMENTS_TABLE.HEADERS.ENGAGEMENT_NAME'),
      accessor: 'EngagementName',
    },
    {
      Header: t('ENGAGEMENTS_TABLE.HEADERS.CLIENT'),
      accessor: 'Client',
    },
    {
      Header: t('ENGAGEMENTS_TABLE.HEADERS.CLIENT_CODE'),
      accessor: 'ClientCode',
    },
    {
      Header: t('ENGAGEMENTS_TABLE.HEADERS.TYPE'),
      accessor: 'Type',
    },
    {
      Header: t('ENGAGEMENTS_TABLE.HEADERS.STATUS'),
      accessor: 'Status',
    },
    {
      Header: t('ENGAGEMENTS_TABLE.HEADERS.MANAGER'),
      accessor: 'Manager',
      Cell: ({ value }) => <Link to={`/professionals/${value}`}>{value}</Link>,
    },
    {
      Header: t('ENGAGEMENTS_TABLE.HEADERS.ENGAGEMENT_RC'),
      accessor: 'EngagementRC',
    },
    {
      Header: t('ENGAGEMENTS_TABLE.HEADERS.ENGAGEMENT_NOTES'),
      accessor: 'EngagementNotes',
      Cell: ({ row }) => (
        <EngagementNotes
          engagementID={row.original.EngagementID}
          engagementNotes={row.original.EngagementNotes}
        />
      ),
    },
    ...(withHours
      ? [
          {
            Header: t('HOURS_REPORT_TABLE.HEADERS.HOURS'),
            accessor: 'Hours',
            Cell: ({ value }) => <HoursDisplay hours={value} />,
          },
        ]
      : []),
  ], [t, withHours]);

  const data = useMemo(() => engagements, [engagements]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({ columns, data }, useFilters, useSortBy);

  return (
    <div className={professionalengagementsCss.sectionContainer}>
      <div className={professionalengagementsCss.sectionHeader}>
        <h3 className={professionalengagementsCss.sectionTitle}>
          {t('ENGAGEMENTS_TABLE.TITLE')}
        </h3>
      </div>
      <div className={professionalengagementsCss.tableWrapper}>
        <div className={professionalengagementsCss.tableContainer}>
          <table {...getTableProps()} className={professionalengagementsCss.table}>
            <thead>
              {headerGroups.map((headerGroup) => {
                const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();
                return (
                  <tr key={key} {...restHeaderGroupProps} className={professionalengagementsCss.headerRow}>
                    {headerGroup.headers.map((column) => {
                      const { key, ...restColumn } = column.getHeaderProps(column.getSortByToggleProps());
                      return (
                        <th
                          key={key}
                          {...restColumn}
                          className={professionalengagementsCss.th}
                        >
                          <div style={{ marginBottom: '0.5rem' }}>
                            {column.render('Header')}
                            <span>
                              {column.isSorted
                                ? column.isSortedDesc
                                  ? ' 🔽'
                                  : ' 🔼'
                                : ''}
                            </span>
                          </div>
                          {column.canFilter && (
                            <input
                              type="text"
                              value={column.filterValue || ''}
                              onChange={(e) => column.setFilter(e.target.value || undefined)}
                              placeholder={`Filter...`}
                              style={{
                                width: '100%',
                                padding: '0.25rem',
                                fontSize: '0.75rem',
                                border: '1px solid #ddd',
                                borderRadius: '3px'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                    No engagements match the current filters
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  prepareRow(row);
                  const { key, ...restRowProps } = row.getRowProps();
                  return (
                    <tr key={key} {...restRowProps} className={professionalengagementsCss.bodyRow}>
                      {row.cells.map((cell) => {
                        const { key, ...restCellProps } = cell.getCellProps();
                        return (
                          <td
                            key={key}
                            {...restCellProps}
                            className={professionalengagementsCss.td}
                          >
                            {cell.render('Cell')}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Engagements;
